import { useState, useCallback, useMemo, useEffect } from 'react';

const TUTORIAL_ID = 'lgl-portal-main';
const STORAGE_KEY_COMPLETED = 'lgl_tutorial_completed';
const STORAGE_KEY_PROGRESS = 'lgl_tutorial_progress';
const STORAGE_KEY_STARTED = 'lgl_tutorial_started';

export const useTutorial = (tutorialSteps, userRole) => {
  // Load saved progress from localStorage
  const getSavedProgress = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_PROGRESS);
      if (saved) {
        const progress = JSON.parse(saved);
        return progress[TUTORIAL_ID] || 0;
      }
    } catch (e) {
      console.error('Error loading tutorial progress:', e);
    }
    return 0;
  };

  const [currentStepIndex, setCurrentStepIndex] = useState(getSavedProgress);
  const [isActive, setIsActive] = useState(false);
  const [completedTutorials, setCompletedTutorials] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY_COMPLETED);
    return saved ? JSON.parse(saved) : [];
  });

  const filteredSteps = useMemo(() => {
    return tutorialSteps.filter(step => {
      if (!step.roles) return true;
      return step.roles.includes(userRole);
    });
  }, [tutorialSteps, userRole]);

  // Save progress whenever step changes
  useEffect(() => {
    if (isActive) {
      try {
        const existingProgress = JSON.parse(localStorage.getItem(STORAGE_KEY_PROGRESS) || '{}');
        existingProgress[TUTORIAL_ID] = currentStepIndex;
        localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(existingProgress));
        // Also mark tutorial as started (so it won't auto-show again)
        localStorage.setItem(STORAGE_KEY_STARTED, 'true');
      } catch (e) {
        console.error('Error saving tutorial progress:', e);
      }
    }
  }, [currentStepIndex, isActive]);

  const finishTutorial = useCallback(() => {
    setIsActive(false);
    if (!completedTutorials.includes(TUTORIAL_ID)) {
      const updated = [...completedTutorials, TUTORIAL_ID];
      setCompletedTutorials(updated);
      localStorage.setItem(STORAGE_KEY_COMPLETED, JSON.stringify(updated));
    }
    // Clear progress since it's completed
    try {
      const existingProgress = JSON.parse(localStorage.getItem(STORAGE_KEY_PROGRESS) || '{}');
      delete existingProgress[TUTORIAL_ID];
      localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(existingProgress));
    } catch (e) {
      console.error('Error clearing tutorial progress:', e);
    }
  }, [completedTutorials]);

  const startTutorial = useCallback((startIndex = null) => {
    // If no explicit start index, resume from saved progress
    if (startIndex === null) {
      const savedProgress = getSavedProgress();
      // Make sure saved progress is within bounds
      const validIndex = Math.min(savedProgress, Math.max(0, filteredSteps.length - 1));
      setCurrentStepIndex(validIndex);
    } else {
      setCurrentStepIndex(startIndex);
    }
    setIsActive(true);
    // Mark as started
    localStorage.setItem(STORAGE_KEY_STARTED, 'true');
  }, [filteredSteps.length]);

  const nextStep = useCallback(() => {
    if (currentStepIndex < filteredSteps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    } else {
      finishTutorial();
    }
  }, [currentStepIndex, filteredSteps.length, finishTutorial]);

  const previousStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((stepIndex) => {
    if (stepIndex >= 0 && stepIndex < filteredSteps.length) {
      setCurrentStepIndex(stepIndex);
    }
  }, [filteredSteps.length]);

  // Close tutorial but save progress (for "X" button or clicking outside)
  const closeTutorial = useCallback(() => {
    setIsActive(false);
    // Progress is already saved via useEffect, just close
    // Mark as started so it won't auto-show on refresh
    localStorage.setItem(STORAGE_KEY_STARTED, 'true');
  }, []);

  // Skip tutorial completely (marks as completed, won't show again)
  const skipTutorial = useCallback(() => {
    setIsActive(false);
    if (!completedTutorials.includes(TUTORIAL_ID)) {
      const updated = [...completedTutorials, TUTORIAL_ID];
      setCompletedTutorials(updated);
      localStorage.setItem(STORAGE_KEY_COMPLETED, JSON.stringify(updated));
    }
    // Clear progress
    try {
      const existingProgress = JSON.parse(localStorage.getItem(STORAGE_KEY_PROGRESS) || '{}');
      delete existingProgress[TUTORIAL_ID];
      localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(existingProgress));
    } catch (e) {
      console.error('Error clearing tutorial progress:', e);
    }
  }, [completedTutorials]);

  const resetTutorial = useCallback(() => {
    setCurrentStepIndex(0);
    setIsActive(false);
    const updated = completedTutorials.filter(id => id !== TUTORIAL_ID);
    setCompletedTutorials(updated);
    localStorage.setItem(STORAGE_KEY_COMPLETED, JSON.stringify(updated));
    // Clear progress and started flag
    try {
      const existingProgress = JSON.parse(localStorage.getItem(STORAGE_KEY_PROGRESS) || '{}');
      delete existingProgress[TUTORIAL_ID];
      localStorage.setItem(STORAGE_KEY_PROGRESS, JSON.stringify(existingProgress));
      localStorage.removeItem(STORAGE_KEY_STARTED);
    } catch (e) {
      console.error('Error resetting tutorial:', e);
    }
  }, [completedTutorials]);

  const currentStep = filteredSteps[currentStepIndex] || null;
  const isCompleted = completedTutorials.includes(TUTORIAL_ID);
  // Has the user ever started (or seen) the tutorial?
  const hasStarted = localStorage.getItem(STORAGE_KEY_STARTED) === 'true';

  return {
    currentStep,
    currentStepIndex,
    totalSteps: filteredSteps.length,
    isActive,
    isCompleted,
    hasStarted,
    startTutorial,
    nextStep,
    previousStep,
    closeTutorial,
    skipTutorial,
    finishTutorial,
    resetTutorial,
    goToStep,
    setIsActive
  };
};

export default useTutorial;
